import { Auth } from "aws-amplify";
import { Icon } from "@shared/ui";
import React, { useContext, useEffect, useState } from "react";

import { AdminContext, ClientContext } from "../App";
import Chart from "../icons/Chart";
import Info from "../icons/Info";
import Link from "../icons/Link";
import SvgLogo from "../icons/Logo";
import Profile from "../icons/Profile";
import Search from "../icons/Search";
import Settings from "../icons/Settings";
import Shield from "../icons/Shield";
import Unfold from "../icons/Unfold";
import Wheel from "../icons/Wheel";
import Pencil from "../icons/Pencil";
import { getClientConfig } from "../lib/automatic";
import { genericClientLogoUrl } from "../lib/constants";
import ClientDropdown from "./ClientDropdown";
import ClientLogoEditDialog from "./ClientLogoEditDialog";
import NavItem from "./NavItem";

const Sidebar = () => {
  const {
    clientConfig: client,
    isPlatformUser,
    setSelectedClient,
    setClientConfig,
    selectedClient: contextClientId,
    isClientTransitioning,
  } = useContext(ClientContext);
  const userRole = useContext(AdminContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pendingClient, setPendingClient] = useState(null);
  const [isInternal, setIsInternal] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        const groups =
          user?.signInUserSession?.accessToken?.payload?.["cognito:groups"] ??
          [];
        setIsInternal(groups.includes("internal-axle"));
      })
      .catch(() => setIsInternal(false));
  }, []);

  // Clear the optimistic pending client once the transition completes
  useEffect(() => {
    if (!isClientTransitioning) setPendingClient(null);
  }, [isClientTransitioning]);

  const handleClientSelect = (selectedClient) => {
    setPendingClient(selectedClient);
    setSelectedClient(selectedClient.id);
    setIsDropdownOpen(false);
  };

  // While transitioning, show the selected client immediately; fall back to confirmed config
  const displayedClient = pendingClient ?? client;

  // Platform-analyst can only access Analytics and Carriers (not Ignition Sessions, Policies, or Team)
  const isPlatformAnalyst = userRole === "platform-analyst";
  const isPlatformRole =
    userRole === "platform-admin" ||
    userRole === "platform-member" ||
    isPlatformAnalyst;
  const canAccessRestrictedPages = !isPlatformAnalyst;

  const canEditClientLogo =
    userRole === "admin" || userRole === "platform-admin";
  const isAdmin = userRole === "admin" || userRole === "platform-admin";

  const baseLogoUrl = displayedClient?.logoUrl ?? genericClientLogoUrl;
  const sidebarLogoUrl =
    logoVersion > 0 && displayedClient?.logoUrl
      ? `${baseLogoUrl}${baseLogoUrl.includes("?") ? "&" : "?"}v=${logoVersion}`
      : baseLogoUrl;

  const handleLogoSaved = async (updated) => {
    const clientId = displayedClient?.id || contextClientId;
    const savedLogoUrl = updated?.logoUrl;

    if (savedLogoUrl) {
      setClientConfig((prev) => ({
        ...prev,
        logoUrl: savedLogoUrl,
      }));
      setLogoVersion(Date.now());
    }

    if (!clientId) {
      return;
    }

    const [configData, error] = await getClientConfig(
      ["validation", "ignition"],
      clientId
    );
    if (configData && !error) {
      setClientConfig(configData);
      setLogoVersion(Date.now());
    }
  };

  const clientHeaderClass = isPlatformUser
    ? "flex gap-x-2 items-center mx-1 cursor-pointer hover:bg-gray-100 p-2 rounded-md transition-colors relative"
    : "flex gap-x-2 items-center mx-1 relative";

  return (
    <div className="flex flex-col h-full w-52 bg-gray-50 px-4 py-6 justify-between fixed z-[9990]">
      <div className="flex flex-col gap-y-8">
        <div
          className={clientHeaderClass}
          onClick={
            isPlatformUser
              ? () => setIsDropdownOpen(!isDropdownOpen)
              : undefined
          }
          title={isPlatformUser ? "Click to change client" : undefined}
        >
          <div className="relative shrink-0">
            <div
              className="inline-block rounded-full h-7 w-7 bg-white bg-5/8 bg-no-repeat bg-cover bg-center"
              style={{
                backgroundImage: `url('${sidebarLogoUrl}')`,
              }}
            />
            {canEditClientLogo && (
              <button
                type="button"
                className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-gray-800 text-white shadow hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400"
                title="Update client logo"
                onClick={(e) => {
                  e.stopPropagation();
                  setLogoDialogOpen(true);
                }}
              >
                <Icon
                  icon={Pencil}
                  height="h-2.5"
                  width="w-2.5"
                  className="text-white"
                />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-hidden flex items-center min-w-0">
            <div className="flex flex-col w-full">
              <h2
                className={`text-sm text-black font-medium truncate transition-opacity duration-200 ${
                  isClientTransitioning ? "opacity-50" : "opacity-100"
                }`}
              >
                {displayedClient?.displayName || "Loading..."}
              </h2>
              {isPlatformUser && displayedClient?.entity && (
                <div className="text-xs text-gray-400 leading-tight truncate">
                  {displayedClient.entity}
                </div>
              )}
            </div>
          </div>
          {isPlatformUser && (
            <div className="flex items-center">
              <Icon
                icon={Unfold}
                height="h-5"
                width="w-5"
                className="text-gray-500"
              />
            </div>
          )}

          {/* Client Dropdown */}
          {isPlatformUser && (
            <ClientDropdown
              isOpen={isDropdownOpen}
              onClose={() => setIsDropdownOpen(false)}
              onClientSelect={handleClientSelect}
            />
          )}
        </div>
        <div className="text-black text-sm flex flex-col gap-y-1">
          {/* Hide Ignition Sessions and Policies for platform-analyst */}
          {canAccessRestrictedPages && (
            <>
              <NavItem
                to={"/ignition"}
                icon={Link}
                label="Ignition Sessions"
                className=""
              />
              <NavItem
                to={"/policies"}
                icon={Shield}
                label="Policies"
                className=""
              />
            </>
          )}
          <NavItem to={"/carriers"} icon={Info} label="Carriers" className="" />

          {canAccessRestrictedPages && (
            <NavItem to={"/team"} icon={Profile} label="Team" className="" />
          )}

          {isPlatformRole && (
            <NavItem to={"/analytics"} icon={Chart} label="Analytics" />
          )}

          {isAdmin && (
            <NavItem to={"/settings"} icon={Settings} label="Settings" />
          )}

          {/* Client Config Comparison Tool — only visible to members of the "internal-axle" Cognito group */}
          {isInternal && (
            <>
              <div className="border-t border-gray-200 my-1" />
              <NavItem
                to={"/internal/clients"}
                icon={Wheel}
                label="Client Config Comparison Tool"
              />
              <NavItem
                to={"/internal/ai"}
                icon={Search}
                label="Axle AI"
                className="pl-10"
              />
            </>
          )}
        </div>
      </div>
      <SvgLogo className="h-4 margin-auto" />
      <ClientLogoEditDialog
        isOpen={logoDialogOpen}
        onClose={() => setLogoDialogOpen(false)}
        clientId={displayedClient?.id || contextClientId}
        clientDisplayName={displayedClient?.displayName}
        clientEntity={displayedClient?.entity}
        currentLogoUrl={displayedClient?.logoUrl}
        onSaved={handleLogoSaved}
      />
    </div>
  );
};

export default Sidebar;
